import { normalizeLicense, LICENSE_PACKAGES, type PlatformLicense, type LicensePackageId } from "@/features/platform-license/contract";
import { normalizeCreatorPackages, type CreatorPackage } from "@/features/creator-packages/contract";
export type AuthorizationOffer = Readonly<{
  version: 1 | 2 | 3;
  avatarId: string;
  avatarTitle: string;
  amountMinor: number;
  currency: "CNY";
  sourceRevision: string;
  quoteHash: string;
  package: Readonly<{
    version: string;
    approved: true;
    name: string;
    scope: string;
    channels: string[];
    region: string;
    exclusive: boolean;
    exclusivityScope?: string;
    creatorPackage?: CreatorPackage;
    platformPackage?: { id: LicensePackageId; name: string; priceFen: number; quantity: number; months: number; summary: string; license: PlatformLicense };
    durationDays: number;
    startsOn: "creator_confirmation";
    deliveryDescription: string;
    licensorDescription: string;
    collectorLegalName: string;
    invoiceIssuerLegalName: string;
  }>;
}>;
export type AuthorizationOfferState = { enabled: false } | { enabled: true; offer: AuthorizationOffer; offers?: AuthorizationOffer[] };
export const isOfferHash = (value: unknown): value is string => typeof value === "string" && /^[a-f0-9]{64}$/u.test(value);
const record = (value: unknown): value is Record<string, unknown> => Boolean(value) && typeof value === "object" && !Array.isArray(value);
const text = (value: unknown, max: number): value is string => typeof value === "string" && Boolean(value.trim()) && value.length <= max;

// Project a fresh public DTO: never forward raw bridge fields or material URLs.
export function parseAuthorizationOffer(value: unknown, allowFree = false): AuthorizationOffer | null {
  if (!record(value) || !record(value.package)) return null;
  const p = value.package;
  if ((value.version !== 1 && value.version !== 2 && value.version !== 3) || !text(value.avatarId, 128) || !/^[A-Za-z0-9_-]+$/u.test(value.avatarId)
    || typeof value.avatarTitle !== "string" || value.avatarTitle.length > 200
    || typeof value.amountMinor !== "number" || !Number.isSafeInteger(value.amountMinor) || value.amountMinor < (allowFree ? 0 : 1)
    || value.currency !== "CNY" || !isOfferHash(value.sourceRevision) || !isOfferHash(value.quoteHash)
    || !text(p.version, 80) || !/^[A-Za-z0-9_.-]+$/u.test(p.version) || p.approved !== true
    || !text(p.name, 120) || !text(p.scope, 2000) || !text(p.region, 200)
    || !Array.isArray(p.channels) || p.channels.length < 1 || p.channels.length > 20
    || !p.channels.every((channel): channel is string => text(channel, 120)) || new Set(p.channels).size !== p.channels.length
    || (value.version === 2 ? p.exclusive !== true : p.exclusive !== false) || typeof p.durationDays !== "number" || !Number.isSafeInteger(p.durationDays) || p.durationDays < 1 || p.durationDays > 3660
    || p.startsOn !== "creator_confirmation" || !text(p.deliveryDescription, 1000) || !text(p.licensorDescription, 500)
    || !text(p.collectorLegalName, 200) || !text(p.invoiceIssuerLegalName, 200)) return null;
  let creatorPackage: CreatorPackage | undefined;
  if (value.version === 2) {
    if (!text(p.exclusivityScope, 500)) return null;
    try { creatorPackage = normalizeCreatorPackages([p.creatorPackage])[0]; } catch { return null; }
    if (creatorPackage.priceFen !== value.amountMinor || creatorPackage.durationDays !== p.durationDays || creatorPackage.name !== p.name) return null;
  }
  let platformPackage: NonNullable<AuthorizationOffer["package"]["platformPackage"]> | undefined;
  if (value.version === 3) {
    if (!record(p.platformPackage)) return null;
    try {
      const raw = p.platformPackage;
      const license = normalizeLicense(raw.license);
      const currentPack = LICENSE_PACKAGES.find(item => item.id === raw.id);
      const pack = currentPack && raw.id === "platform_tvc" && raw.months === 0 && raw.summary === "单条成片 · 期限以订单约定为准"
        ? { ...currentPack, months: 0, summary: "单条成片 · 期限以订单约定为准" } : currentPack;
      if (!pack || !license.packages.includes(pack.id) || p.platformPackage.priceFen !== value.amountMinor
        || (value.amountMinor !== pack.priceFen && value.amountMinor !== 1) || p.platformPackage.quantity !== pack.quantity
        || p.platformPackage.months !== pack.months || p.name !== pack.name || p.version !== license.version) return null;
      platformPackage = { ...pack, priceFen: value.amountMinor, license };
    } catch { return null; }
  }
  return { version: value.version as AuthorizationOffer["version"], avatarId: value.avatarId, avatarTitle: value.avatarTitle, amountMinor: value.amountMinor, currency: "CNY",
    sourceRevision: value.sourceRevision, quoteHash: value.quoteHash,
    package: { version: p.version, approved: true, name: p.name, scope: p.scope, channels: [...p.channels], region: p.region,
      exclusive: p.exclusive as boolean, ...(platformPackage ? { platformPackage } : {}), ...(creatorPackage ? { creatorPackage, exclusivityScope: p.exclusivityScope as string } : {}), durationDays: p.durationDays, startsOn: "creator_confirmation", deliveryDescription: p.deliveryDescription,
      licensorDescription: p.licensorDescription, collectorLegalName: p.collectorLegalName, invoiceIssuerLegalName: p.invoiceIssuerLegalName } };
}
